import { getAuth, getIdToken } from "firebase/auth";
import axiosClient from "../../apis/axios-client";
import { API_CONFIG } from "../../config/api";
import { InterpretHttpDeps } from "../core/interpretClient";
import { TranscribeHttpDeps } from "../core/transcribeClient";
import { InterpretResponse, TranscribeResponse } from "../core/types";
import { voiceDebugEnabled } from "../core/perf";

/**
 * Satisfies core/interpretClient.ts's injected-deps contract using this
 * app's existing axios instance and Firebase auth — same conventions every
 * other API call in this app already follows.
 */
export const voiceHttpDeps: InterpretHttpDeps = {
  // axiosClient's response interceptor already unwraps to response.data and
  // attaches the Authorization header itself; `token` is accepted here only
  // for interface parity with a future native implementation that needs it
  // explicitly.
  post: async (path, body) => {
    // axiosClient's response interceptor already unwraps to response.data at
    // runtime, but axios's own types don't reflect that — matches the cast
    // this codebase's other API modules rely on.
    return axiosClient.post<InterpretResponse>(path, body) as unknown as Promise<InterpretResponse>;
  },
  getAuthToken: async () => {
    const user = getAuth().currentUser;
    if (!user) return null;
    return getIdToken(user);
  },
  generateId: () => crypto.randomUUID(),
};

/**
 * Audio upload for POST /Voice/Transcribe.
 *
 * Two deliberate overrides of the shared axios instance:
 *
 * 1. `Content-Type: undefined` deletes the instance-level
 *    `application/json` default. Axios only generates the multipart boundary
 *    when no Content-Type is already set, so leaving the default in place
 *    ships a body the server cannot parse.
 * 2. `UPLOAD_TIMEOUT` instead of the 30s default. The machine applies its own
 *    shorter deadline; this one exists only so axios doesn't abort first on a
 *    slow uplink.
 *
 * POST is not in API_CONFIG.RETRYABLE_METHODS, so a failed upload is never
 * retried automatically — the audio is billed per request downstream and a
 * silent retry would double-charge it.
 */
/**
 * Debug-only escape hatch. The local containsSpeech() gate runs on the
 * AudioWorklet’s PCM, while what gets uploaded is a separate MediaRecorder
 * capture of the same stream — so “the gate passed” does not prove the blob
 * has audio in it. This publishes the exact bytes that went on the wire so
 * they can be played back, which is the only way to tell a silent recording
 * apart from a server that failed to decode a good one.
 *
 *   localStorage.setItem("voice:debug", "1")   // then reload and record
 *   window.__lastVoiceClip                      // { url, size, type }
 */
function publishDebugClip(form: FormData): void {
  if (!voiceDebugEnabled()) return;
  try {
    const audio = form.get("Audio");
    if (!(audio instanceof Blob)) return;
    const previous = (window as unknown as { __lastVoiceClip?: { url: string } }).__lastVoiceClip;
    if (previous?.url) URL.revokeObjectURL(previous.url);
    const entry = { url: URL.createObjectURL(audio), size: audio.size, type: audio.type };
    (window as unknown as { __lastVoiceClip?: unknown }).__lastVoiceClip = entry;
    // eslint-disable-next-line no-console
    console.info("[voice:perf] upload.clip", entry);
  } catch {
    // Diagnostics must never break the upload they are diagnosing.
  }
}

export const voiceTranscribeHttpDeps: TranscribeHttpDeps = {
  postMultipart: async (path, form, signal) => {
    publishDebugClip(form);
    return axiosClient.post<TranscribeResponse>(path, form, {
      headers: { "Content-Type": undefined },
      timeout: API_CONFIG.UPLOAD_TIMEOUT,
      signal,
    }) as unknown as Promise<TranscribeResponse>;
  },
  generateId: () => crypto.randomUUID(),
};
